import React, { useEffect, useState, Fragment } from 'react';
import DashboardLayout from '../components/Nav';
import toast from 'react-hot-toast';
import { Dialog, Transition } from '@headlessui/react';
import { PencilIcon, TrashIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import getClientConfig from '../config';

const { API_BASE_URL } = getClientConfig();

export default function NASManagementPage() {
  const [nasList, setNasList] = useState([]);
  const [newNAS, setNewNAS] = useState({ name: '', ip_address: '' });
  const [editNAS, setEditNAS] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [token, setToken] = useState('');

  useEffect(() => {
    document.title = "NAS Management | IP Intelligence Portal";
  }, []);

  useEffect(() => {
    const auth = localStorage.getItem('auth_token');
    if (!auth) return;
    setToken(auth);
  }, []);

  useEffect(() => {
    if (token) fetchNAS();
  }, [token]);

  const fetchNAS = () => {
    fetch(`${API_BASE_URL}/nas-list`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(res => res.json())
      .then(data => setNasList(data || []));
  };

  const post = async (endpoint, method = 'POST', body) => {
    const res = await fetch(`${API_BASE_URL}/${endpoint}`, {
      method,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data?.error || 'Something went wrong');
    fetchNAS();
    return data;
  };

  const handleAdd = async () => {
    try {
      await post('nas', 'POST', newNAS);
      toast.success('NAS added');
      setNewNAS({ name: '', ip_address: '' });
      setIsAddModalOpen(false);
    } catch (err) {
      if (err.message.includes('SQLSTATE 23505')) {
        toast.error(`NAS ${newNAS.ip_address} already added.`);
      } else {
        toast.error(err.message);
      }
    }
  };
  

  const handleEdit = async () => {
    try {
      await post(`nas/${editNAS.id}`, 'PUT', editNAS);
      toast.success('NAS updated');
      setEditNAS(null);
      setIsEditModalOpen(false);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    try {
      await post(`nas/${id}`, 'DELETE', {});
      toast.success('NAS deleted');
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold">Manage NAS Sources</h2>
          <button
            className="flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
            onClick={() => setIsAddModalOpen(true)}
          >
            <PlusIcon className="w-5 h-5" />
            Add NAS
          </button>
        </div>

        <table className="w-full border text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th className="p-3 text-left">IP Address</th>
              <th className="p-3 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {nasList.map(nas => (
              <tr key={nas.id} className="border-t hover:bg-gray-50">
                <td className="p-3">{nas.name}</td>
                <td className="p-3">{nas.ip_address}</td>
                <td className="p-3 text-center space-x-2">
                  <button
                    className="text-blue-600 hover:text-blue-800"
                    onClick={() => {
                      setEditNAS({ ...nas });
                      setIsEditModalOpen(true);
                    }}
                  >
                    <PencilIcon className="w-5 h-5 inline" />
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800"
                    onClick={() => handleDelete(nas.id)}
                  >
                    <TrashIcon className="w-5 h-5 inline" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add Modal */}
      <Transition appear show={isAddModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsAddModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title className="text-lg font-semibold">Add New NAS</Dialog.Title>
                <XMarkIcon className="w-5 h-5 cursor-pointer" onClick={() => setIsAddModalOpen(false)} />
              </div>
              <div className="space-y-4">
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Name"
                  value={newNAS.name}
                  onChange={e => setNewNAS({ ...newNAS, name: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="IP Address"
                  value={newNAS.ip_address}
                  onChange={e => setNewNAS({ ...newNAS, ip_address: e.target.value })}
                />
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  onClick={handleAdd}
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>

      {/* Edit Modal */}
      <Transition appear show={isEditModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsEditModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200" enterFrom="opacity-0" enterTo="opacity-100"
            leave="ease-in duration-150" leaveFrom="opacity-100" leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/50" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto flex items-center justify-center p-4">
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
              <div className="flex justify-between items-center mb-4">
                <Dialog.Title className="text-lg font-semibold">Edit NAS</Dialog.Title>
                <XMarkIcon className="w-5 h-5 cursor-pointer" onClick={() => setIsEditModalOpen(false)} />
              </div>
              <div className="space-y-4">
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="Name"
                  value={editNAS?.name || ''}
                  onChange={e => setEditNAS({ ...editNAS, name: e.target.value })}
                />
                <input
                  className="w-full border px-4 py-2 rounded"
                  placeholder="IP Address"
                  value={editNAS?.ip_address || ''}
                  onChange={e => setEditNAS({ ...editNAS, ip_address: e.target.value })}
                />
              </div>
              <div className="mt-6 flex justify-end">
                <button
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  onClick={handleEdit}
                >
                  Save
                </button>
              </div>
            </Dialog.Panel>
          </div>
        </Dialog>
      </Transition>
    </DashboardLayout>
  );
}
